import { Link } from "react-router-dom";
import { useForgotPassword } from "../../hooks/auth/useForgotPassword";
import {
  forgotPasswordSchema,
  type ForgotPasswordInput,
} from "../../utils/zod/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { InputField } from "../../components/common/ui/inputField";
import { SelectField } from "../../components/common/ui/select/selectField";
import { ConstellationBackground } from "../../components/common/layout/constellationBackground";
import { ThemedLogo } from "../../components/common/ui/themedLogo";

export const ForgotPasswordPage = () => {
  const { mutate, isPending, availableGyms, resetGymSelection } =
    useForgotPassword();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "", gymSlug: "" },
  });

  const gymSlug = watch("gymSlug");
  const requiresGymSelection =
    Array.isArray(availableGyms) && availableGyms.length > 0;
  const gymSelectionError =
    requiresGymSelection && isSubmitted && !gymSlug
      ? "Selecciona un gimnasio para continuar"
      : undefined;

  return (
    <div className="min-h-screen flex flex-col bg-global-page relative isolate">
      <ConstellationBackground />
      <div className="flex-1 flex items-center justify-center px-3 sm:px-4 py-6 sm:py-8">
        <div className="w-full max-w-md">
          <div className="bg-global-overlay backdrop-blur-sm border border-global-primary shadow-2xl rounded-xl sm:rounded-2xl overflow-hidden">
            <div className="px-6 sm:px-8 pt-7 sm:pt-9 pb-5 sm:pb-6 text-center border-b border-global-primary">
              <ThemedLogo className="mx-auto h-12 w-auto" />
              <p className="text-[10px] text-global-muted tracking-[0.25em] uppercase mt-1">
                Software
              </p>
              <h2 className="mt-4 text-lg sm:text-xl font-semibold tracking-tight text-global-title">
                Recuperar contraseña
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-global-accent">
                Ingresa tu correo y te enviaremos un enlace para restablecerla
              </p>
            </div>

            <div className="px-5 sm:px-6 md:px-8 py-5 sm:py-6 md:py-7">
              <form
                className="space-y-4 sm:space-y-5"
                onSubmit={handleSubmit((data) =>
                  mutate(data, {
                    onSuccess: () => resetGymSelection(),
                  }),
                )}
              >
                <div className="space-y-1.5 sm:space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-global-label">
                    Correo Electrónico
                  </label>
                  <InputField
                    type="email"
                    placeholder="tu@correo.com"
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="text-size-2xs text-global-error">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {requiresGymSelection && (
                  <div className="space-y-1.5 rounded-lg border border-global-primary bg-global-overlay-secondary p-3">
                    <p className="text-xs text-global-accent">
                      Tu correo está asociado a más de un gimnasio. Elegí a
                      cuál corresponde la cuenta para recuperar la contraseña.
                    </p>
                    <SelectField
                      id="forgot-gym-slug"
                      label={
                        <span className="text-xs font-medium text-global-label">
                          Gimnasio
                        </span>
                      }
                      options={[
                        { value: "", label: "Selecciona un gimnasio" },
                        ...availableGyms!.map((g) => ({
                          value: g.slug,
                          label: g.name,
                        })),
                      ]}
                      error={gymSelectionError}
                      {...register("gymSlug")}
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={
                    isPending || (requiresGymSelection && !gymSlug)
                  }
                  className="mt-2 inline-flex w-full items-center justify-center rounded-lg btn-global-primary-gradient px-4 py-3 sm:py-2.5 text-sm sm:text-base font-bold shadow-global-button focus:outline-none focus:ring-2 focus-global-ring focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition touch-manipulation uppercase tracking-wide"
                >
                  {isPending ? (
                    <div className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Enviar enlace</span>
                    </div>
                  ) : (
                    "Enviar enlace"
                  )}
                </button>
              </form>

              <p className="mt-5 text-center">
                <Link
                  to="/auth"
                  className="text-sm text-global-accent hover:underline"
                >
                  Volver al inicio de sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
